import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { AuthScreen } from '@/components/AuthScreen/AuthScreen';
import { SignInForm, type SignInStage } from '@/components/SignInForm/SignInForm';
import { environmentName } from '@/config/runtime-config';
import { useToast } from '@/toast/useToast';

/**
 * Owns the sign-in conversation: which step we are on, the challenge id in
 * play, what is in flight and what failed. `SignInForm` renders it and knows
 * none of it.
 */
export function SignInContainer() {
  const { requestPin, submitPin } = useAuth();
  const toast = useToast();
  const environment = useMemo(() => environmentName(), []);
  const [stage, setStage] = useState<SignInStage>('email');
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onRequestPin = useCallback(
    async (address: string) => {
      setPending(true);
      setError(null);
      try {
        setChallengeId(await requestPin(address));
        setEmail(address);
        setStage('pin');
        toast.success({ title: 'Pin on its way', description: `Sent to ${address}` });
      } catch {
        // Turnstile answers 202 whether or not a pin was sent, so anything
        // that lands here is a transport or validation failure, never "we do
        // not know that address".
        setError('Could not request a pin. Check the address and try again.');
      } finally {
        setPending(false);
      }
    },
    [requestPin, toast],
  );

  const onSubmitPin = useCallback(
    async (pin: string) => {
      if (!challengeId) {
        return;
      }
      setPending(true);
      setError(null);
      try {
        await submitPin(challengeId, pin);
        toast.success({ title: 'Welcome back', description: `Signed in as ${email}` });
      } catch {
        setError('That pin was not accepted. Request a new one if it has expired.');
      } finally {
        setPending(false);
      }
    },
    [challengeId, submitPin, toast, email],
  );

  const onStartOver = useCallback(() => {
    setStage('email');
    setChallengeId(null);
    setError(null);
  }, []);

  return (
    <AuthScreen environment={environment}>
      <SignInForm
        // Remounts the form on step change so its inputs start empty.
        key={stage}
        stage={stage}
        email={email}
        pending={pending}
        error={error}
        onRequestPin={onRequestPin}
        onSubmitPin={onSubmitPin}
        onStartOver={onStartOver}
      />
    </AuthScreen>
  );
}
